/* Homepage collection cards reveal once without moving or resizing their photos.
   Native content remains visible if motion or observation is unavailable. */
(() => {
  'use strict';
  const revealed = new WeakSet();
  let dispose = () => {};
  let mountedSection = null, mountedRail = null, mountedCards = [], contentObserver;

  function mount() {
    const section = document.querySelector('.home-categories');
    const rail = section?.querySelector('.home-category-rail') || null;
    const cards = section ? [...section.querySelectorAll('.home-category')] : [];
    if (section === mountedSection && rail === mountedRail && cards.length === mountedCards.length &&
      cards.every((card, index) => card === mountedCards[index])) return;
    dispose();
    mountedSection = section; mountedRail = rail; mountedCards = cards;
    if (!section || !cards.length) { dispose = () => {}; return; }
    const controller = new AbortController(), {signal} = controller;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const pending = new Set();
    let disposed = false, frame = 0, revealObserver;

    function inView(card) {
      const rect = card.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
    }
    function cancelFrame() { if (frame) cancelAnimationFrame(frame); frame = 0; }
    function reveal(card) {
      card.classList.remove('is-category-pending');
      pending.delete(card); revealed.add(card);
      revealObserver?.unobserve(card);
    }
    function clearMotion() {
      cancelFrame(); revealObserver?.disconnect(); pending.clear();
      section.classList.remove('tjs-category-reveal-ready');
      cards.forEach(card => card.classList.remove('is-category-pending'));
    }
    function revealVisible() {
      frame = 0;
      if (disposed || document.hidden) return;
      for (const card of pending) if (inView(card)) reveal(card);
    }
    function schedule() {
      if (!frame && !disposed && !document.hidden && pending.size) frame = requestAnimationFrame(revealVisible);
    }
    function configure() {
      clearMotion();
      if (reduced.matches || typeof IntersectionObserver === 'undefined') {
        cards.forEach(card => revealed.add(card)); return;
      }
      try {
        revealObserver = new IntersectionObserver(entries => {
          if (disposed) return;
          for (const entry of entries) if (entry.isIntersecting) reveal(entry.target);
        }, {threshold: .08});
        for (const card of cards) {
          if (revealed.has(card) || inView(card) || card.contains(document.activeElement)) reveal(card);
          else { pending.add(card); card.classList.add('is-category-pending'); revealObserver.observe(card); }
        }
        section.classList.add('tjs-category-reveal-ready');
      } catch (_) {
        clearMotion(); cards.forEach(card => revealed.add(card));
      }
    }
    // Scroll/resize checks also keep cards readable if an observer is delayed.
    window.addEventListener('scroll', schedule, {passive: true, signal});
    window.addEventListener('resize', schedule, {passive: true, signal});
    rail?.addEventListener('scroll', schedule, {passive: true, signal});
    section.addEventListener('focusin', event => {
      const card = event.target.closest('.home-category');
      if (cards.includes(card)) reveal(card);
    }, {signal});
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) cancelFrame(); else schedule();
    }, {signal});
    reduced.addEventListener('change', configure, {signal});
    configure();
    dispose = () => { disposed = true; controller.abort(); clearMotion(); };
  }

  function watchContent() {
    const main = document.getElementById('main');
    if (main && typeof MutationObserver !== 'undefined') {
      contentObserver ||= new MutationObserver(mount);
      contentObserver.observe(main, {childList: true, subtree: true});
    }
    mount();
  }
  window.addEventListener('hashchange', mount);
  window.addEventListener('pagehide', () => {
    contentObserver?.disconnect(); dispose();
    mountedSection = null; mountedRail = null; mountedCards = [];
  });
  window.addEventListener('pageshow', event => { if (event.persisted) watchContent(); });
  watchContent();
})();
